import React, { useState } from 'react';
import { View, StyleSheet } from 'react-native';
import { AggReportSetupView, AggReportView, DataExportSetupView, DataExportView, StatsSubsectionPlaceholderView, UsageView } from './stats';
import { useActiveAccountStore } from '../../stores/activeAccountStore';
import { resolveAuthContext } from '../../services/auth/authContext';
import { EDITOR_COLORS } from '../../styles/editorTheme';

interface ProGroupsStatsViewProps {
  storage?: any;
  apiService?: any;
  initialSubsection?: string;
}

type StatsSubsection = 'Usage' | 'Scheduled' | 'Aggregate' | 'System' | 'Data Export';

const resolveStatsSubsection = (initialSubsection?: string): StatsSubsection => {
  switch (initialSubsection) {
    case 'Usage':
    case 'Scheduled':
    case 'Aggregate':
    case 'System':
    case 'Data Export':
      return initialSubsection;
    default:
      return 'Usage';
  }
};

const resolveApiService = (apiService?: any): any => {
  return apiService
    || (globalThis as any).__proGroupsApiService
    || (globalThis as any).__childNavApiService;
};

const resolveUserStore = (): any => resolveAuthContext().userStore;

const resolveAccountId = (): string | null => {
  const userStore = resolveUserStore();
  const state = userStore?.getState ? userStore.getState() : null;
  return state?.session?.user?.familyId
    || state?.session?.familyAccount?.familyId
    || null;
};

export const ProGroupsStatsView: React.FC<ProGroupsStatsViewProps> = ({
  storage,
  apiService,
  initialSubsection,
}) => {
  const activeSubsection = resolveStatsSubsection(initialSubsection);
  const resolvedApiService = resolveApiService(apiService);

  // Same source-of-truth pattern as ProGroupsAccountView: activeAccountStore first (elevated
  // users viewing a different account), falling back to the signed-in user's own account.
  const activeAccountId = useActiveAccountStore((state) => state.activeAccountId);
  const accountId = activeAccountId || resolveAccountId();

  // The Aggregate subtab has no real routing — like AccountSettingsView's own list⇄detail
  // toggle, Setup is just a local mode swap within this one branch, not a separately routed
  // screen (there's no push/pop nav primitive anywhere in this shell to hook into).
  const [aggMode, setAggMode] = useState<'view' | 'setup'>('view');
  const [aggSetupReportId, setAggSetupReportId] = useState<string>('');

  // Data Export Setup is the same kind of local mode swap. Selected variables are kept per
  // assessment in memory for the session (not persisted).
  const [exportMode, setExportMode] = useState<'view' | 'setup'>('view');
  const [exportAssessmentId, setExportAssessmentId] = useState<string>('');
  const [exportVarIds, setExportVarIds] = useState<Record<string, string[]>>({});

  return (
    <View style={styles.container}>
      {activeSubsection === 'Usage' ? (
        <UsageView
          storage={storage}
          apiService={resolvedApiService}
          accountId={accountId}
        />
      ) : activeSubsection === 'Aggregate' ? (
        aggMode === 'setup' ? (
          <AggReportSetupView
            storage={storage}
            apiService={resolvedApiService}
            accountId={accountId}
            initialReportId={aggSetupReportId}
            onBack={() => setAggMode('view')}
            // Stay on the setup form after a save — just remember which report was saved so the
            // report view preselects it once the user follows the Back link.
            onSaved={(reportId) => setAggSetupReportId(reportId)}
          />
        ) : (
          <AggReportView
            storage={storage}
            apiService={resolvedApiService}
            accountId={accountId}
            initialReportId={aggSetupReportId}
            onOpenSetup={(reportId) => {
              setAggSetupReportId(reportId);
              setAggMode('setup');
            }}
          />
        )
      ) : activeSubsection === 'Data Export' ? (
        exportMode === 'setup' ? (
          <DataExportSetupView
            storage={storage}
            assessmentId={exportAssessmentId}
            selectedIds={exportVarIds[exportAssessmentId] ?? []}
            onChange={(ids) => setExportVarIds((prev) => ({ ...prev, [exportAssessmentId]: ids }))}
            onBack={() => setExportMode('view')}
          />
        ) : (
          <DataExportView
            storage={storage}
            apiService={resolvedApiService}
            accountId={accountId}
            varIdsByAssessment={exportVarIds}
            initialAssessmentId={exportAssessmentId}
            onOpenSetup={(assessmentId) => {
              setExportAssessmentId(assessmentId);
              setExportMode('setup');
            }}
          />
        )
      ) : (
        <StatsSubsectionPlaceholderView subsectionTitle={activeSubsection} />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: EDITOR_COLORS.background,
  },
});
